import { useNavigate, useParams } from 'react-router-dom';
import { IdentificationIcon, BuildingOffice2Icon, SquaresPlusIcon, PlusSmallIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import useApi from "../../../utility/Hooks/Api/useApi";
import { CompanyApi } from "../../../services/Api/CompanyApi";
import VehicleSelect from "../../../components/Selects/Vehicle/VehicleSelect";
import { MinusCircleIcon } from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleXmark } from "@fortawesome/free-solid-svg-icons";

export default function CompanyDetailView() {
    const { companyId: id } = useParams();
    const [editableCompanyData, setEditableCompanyData] = useState({});
    const { request: companyRequest, data: companyData, loading: companyLoading, set: vehicleSetEhuData } = useApi();
    const { request: companyVehicleRequest, data: companyVehicleData, set: companySetVehicleData } = useApi();
    const { request: linkRequest, data: linkData, loading: linkLoading } = useApi();
    const [showSelect, setShowSelect] = useState(false);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchDadata = async () => {
            try {
                await companyRequest(CompanyApi.getCompany(id));
                await companyVehicleRequest(CompanyApi.getVehicles(id));
            } catch (error) {

            } finally {
                setLoading(false);
            };
        };

        fetchDadata();
    }, [id]);

    useEffect(() => {
        setEditableCompanyData(companyData)
    }, [companyData])

    const getCaseInsensitive = (obj, key) => {
        if (!obj) return undefined;
        const lowerCaseKey = key.toLowerCase();
        return Object.keys(obj).reduce((result, currentKey) => {
            return currentKey.toLowerCase() === lowerCaseKey ? obj[currentKey] : result;
        }, undefined);
    };

    const certificateHandler = (e) => {
        const certificate = e?.find(e => e.text.includes("certificateNumber"))?.["text"];
        return certificate ? certificate.split(": ").pop() : null;
    };

    const addVehicle = (e) => {
        const vehicle = JSON.parse(e.value);
        linkRequest(CompanyApi.addVehicle(id, getCaseInsensitive(vehicle, "id")));
        companyVehicleData.push(vehicle);
        setShowSelect(false);
    };

    const removeVehicle = (vehicleId) => {
        linkRequest(CompanyApi.removeVehicle(id, vehicleId));
        const newArr = companyVehicleData.filter(vehicle => {
            return getCaseInsensitive(vehicle, "id") !== vehicleId;
        });
        companySetVehicleData([...newArr]);
    };

    const EditableField = ({ label, value, backgroundColor }) => {
        return (
            <div className={`flex py-2 border-t border-blue-100 px-4 text-sm ${backgroundColor}`}>
                <div className="w-1/2 font-semibold">{label}</div>
                <div className="w-1/2">
                    <span className="text-gray-700">
                        {value ? value : 'N/A'}
                    </span>
                </div>
            </div>
        );
    };

    const EditableFieldsList = ({
        type,
        editableData,
    }) => {

        const basicInformation = [
            { label: "Customer ID", value: getCaseInsensitive(editableData, "customer id"), field: "customer id" },
            { label: "Type", value: getCaseInsensitive(editableData["type"], "Name"), field: "name" },
            { label: "CCO", value: getCaseInsensitive(editableData, "cco"), field: "cco" },
            { label: "VAT", value: getCaseInsensitive(editableData, "vat"), field: "vat" },
        ];

        const adressInformation = [
            { label: "Country", value: getCaseInsensitive(editableData, "country"), field: "country" },
            { label: "Province", value: getCaseInsensitive(editableData, "province",), field: "province" },
            { label: "City", value: getCaseInsensitive(editableData, "city"), field: "city" },
            { label: "Street", value: getCaseInsensitive(editableData, "street name"), field: "street name" },
            { label: "Street Nr.", value: getCaseInsensitive(editableData, "street number"), field: "street number" },
            { label: "Postal Code", value: getCaseInsensitive(editableData, "postal code"), field: "postal code" },
        ];

        return (
            <div className="border border-blue-100 rounded w-full">
                {/* Column headers */}
                <div className="flex py-2 px-4 font-bold text-gray-800 bg-blue-100/75 text-sm">
                    <div className="w-1/2">Field</div>
                    <div className="w-1/2">Value</div>
                </div>

                {/* List of fields */}
                {(type === "Company Information" ? basicInformation : adressInformation).map((item, index) => (
                    <EditableField
                        key={item.field}
                        label={item.label}
                        value={item.value}
                        backgroundColor={index % 2 === 0 ? 'bg-white' : 'bg-blue-100/20'}
                    />
                ))}
            </div>
        );
    };

    const InfoSection = ({
        title,
        companyData
    }) => {
        const [editableCompanyData, setEditableCompanyData] = useState({ ...companyData });

        return (
            <div className="w-full px-4">
                <div className="flex items-center justify-between border-b-2 border-gray-100 mt-2 mb-4">
                    <h3 className="font-semibold text-lg text-gray-800">{title}</h3>
                </div>
                <EditableFieldsList
                    type={title}
                    editableData={editableCompanyData}
                />
            </div>
        );
    };

    return (
        <>
            {!loading ?
                <div className="relative container mx-auto bg-white rounded-lg shadow-2xl min-h-[46.4rem]">
                    <div className="mb-1 sm:mb-4 justify-between w-full items-center">
                        <div>
                            <div className="mb-0 px-4 pt-4 border-0">
                                <div className="flex lg:flex-row flex-col">
                                    <div className="relative px-4 max-w-full">
                                        <h3 className="font-semibold text-lg text-gray-800">{getCaseInsensitive(companyData, "company name")}</h3>
                                    </div>
                                    <div className="relative px-4 max-w-full flex lg:flex-col text-sm lg:border-l-2">
                                        <span className="flex lg:mr-0 mr-1">Type:</span>
                                        <span className="flex text-indigo-600">{getCaseInsensitive(companyData["type"], "Name")}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className="rounded-t mb-0 pt-1 pb-4 px-4 border-0">
                                    <div className="relative px-4 max-w-full flex flex-row items-center">
                                        <IdentificationIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                                        <span className="text-gray-500 px-2 text-sm">{id}</span>
                                    </div>
                                    <div className="relative px-4 max-w-full flex flex-row items-center">
                                        <BuildingOffice2Icon className="h-5 w-5 text-gray-600" aria-hidden="true" />
                                        <span className="text-gray-500 px-2 text-sm">{getCaseInsensitive(companyData, "customer id")}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="relative lg:flex mb-1 sm:mb-4">
                                <div className="flex flex-row mb-1 justify-between w-full lg:mr-2">
                                    <div className="mb-0 px-4 py-4 w-full">
                                        <InfoSection
                                            title="Company Information"
                                            companyData={editableCompanyData}
                                        ></InfoSection>
                                    </div>
                                </div>
                                <div className="flex flex-row mb-1 justify-between w-full lg:ml-2">
                                    <div className="mb-0 px-4 py-4 border-0 w-full">
                                        <InfoSection
                                            title="Adress"
                                            companyData={editableCompanyData}
                                        ></InfoSection>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row justify-between w-full p-4">
                                <div className="mb-0 px-4 border-0 w-full">
                                    <div className="flex flex-wrap items-center">
                                        <div className="flex flex-col w-full">
                                            <div className={`relative w-full max-w-full flex-grow flex-1 flex mt-2 mb-4 justify-between border-b-2 border-gray-100 items-center ${showSelect ? "" : "mb-4"}`}><h3
                                                className="font-semibold text-lg text-gray-800">Vehicles</h3>
                                                {!showSelect ? (
                                                    <SquaresPlusIcon className="h-5 w-5 cursor-pointer text-gray-600 hover:text-green-400" onClick={() => setShowSelect(!showSelect)} />
                                                ) : (
                                                    <div className="flex space-x-2">

                                                        <FontAwesomeIcon
                                                            icon={faRectangleXmark}
                                                            className="h-5 w-5 text-gray-600 hover:text-red-400 cursor-pointer"
                                                            onClick={() => setShowSelect(!showSelect)}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`w-full pb-2 ${showSelect ? "" : "hidden"}`}>
                                                <VehicleSelect classname="w-full" required={true}
                                                    onChange={(vehicle) => addVehicle(vehicle)} />
                                            </div>
                                            {/* <div className={`w-full mb-4 ${showSelect ? "" : "hidden"}`}>
                                                    <PartsSelect classname="w-full" required={true} onChange={(part) => addPart(part)} />
                                                </div> */}
                                        </div>
                                        <div className='w-full border border-blue-100 rounded'>
                                            <div className="flex py-2 px-4 bg-blue-100/75 font-semibold">
                                                <div className="flex-1 text-sm">Reference</div>
                                                <div className="flex-1 text-sm">Number Plate</div>
                                                <div className="flex-1 text-sm">Type</div>
                                                <div className="flex w-1/12 justify-center"></div>
                                            </div>
                                            <ul>
                                                {
                                                    companyVehicleData.map((vehicle, index) => {
                                                        // Determine the background color based on the index
                                                        const backgroundColor = index % 2 === 0 ? 'bg-white' : 'bg-blue-100/20';

                                                        return (
                                                            <li key={vehicle["id"]} className={`flex py-2 px-4 border-t border-blue-100 ${backgroundColor}`}>

                                                                <div onClick={() => navigate(`/main/vehicle/${vehicle["id"]}`)} className="flex-1 text-sm hover:text-indigo-400 cursor-pointer font-semibold">   {getCaseInsensitive(vehicle, "reference")} </div>
                                                                <div title={vehicle["plateNumber"]} className="flex-1 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{getCaseInsensitive(vehicle, "plateNumber")}</div>
                                                                <div title={vehicle["type"]} className="flex-1 text-sm whitespace-nowrap overflow-hidden text-ellipsis">{getCaseInsensitive(vehicle, "type")}</div>


                                                                <div className="flex w-1/12 justify-end items-center">
                                                                    <MinusCircleIcon
                                                                        className="h-5 w-5 text-red-600 cursor-pointer hover:text-red-400"
                                                                        onClick={(e) => removeVehicle(getCaseInsensitive(vehicle, "id"))}
                                                                    />
                                                                </div>
                                                            </li>
                                                        );
                                                    })
                                                }
                                            </ul>
                                        </div>
                                        {/* <div className={`w-full mt-4 ${showSelect ? "" : "hidden"}`}>
                                                <PartsSelect classname="w-full" required={true} onChange={(part) => addPart(part)} />
                                            </div> */}
                                    </div>
                                </div>
                            </div>








                        </div>
                    </div>
                </div>
                : null}
        </>
    )
}
